/**
 * Authentication store (Zustand). Wraps `authService` and persists the JWT to
 * `localStorage[TOKEN_STORAGE_KEY]` so sessions survive reloads. A companion
 * key holds the minimal user profile ({ id, email }) for header display.
 *
 * Actions set `error` on failure AND re-throw so callers can react (e.g. keep
 * the user on the form). Read `error`/`loading` for declarative UI states.
 */
import { create } from 'zustand';
import * as authService from '@/services/authService';
import { ApiRequestError, TOKEN_STORAGE_KEY } from '@/utils/api';

/** localStorage key holding the cached `{ id, email }` profile. */
const USER_STORAGE_KEY = `${TOKEN_STORAGE_KEY}_user`;

/** Minimal authenticated-user shape kept in the store. */
export interface AuthUser {
  id: string;
  email: string;
}

/** Auth store state + actions. */
export interface AuthState {
  token: string | null;
  user: AuthUser | null;
  isAuthenticated: boolean;
  loading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string) => Promise<void>;
  logout: () => void;
  loadFromStorage: () => void;
  clearError: () => void;
}

/** Extract a user-facing message from an unknown thrown value. */
function toMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiRequestError) return err.message;
  if (err instanceof Error) return err.message;
  return fallback;
}

/** Persist token + profile to localStorage. */
function persistSession(token: string, user: AuthUser): void {
  localStorage.setItem(TOKEN_STORAGE_KEY, token);
  localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user));
}

/** Clear any persisted session. */
function clearSession(): void {
  localStorage.removeItem(TOKEN_STORAGE_KEY);
  localStorage.removeItem(USER_STORAGE_KEY);
}

export const useAuthStore = create<AuthState>()((set) => ({
  token: null,
  user: null,
  isAuthenticated: false,
  loading: false,
  error: null,

  login: async (email, password) => {
    set({ loading: true, error: null });
    try {
      const res = await authService.login(email, password);
      const user: AuthUser = { id: res.userId, email: res.email };
      persistSession(res.token, user);
      set({ token: res.token, user, isAuthenticated: true, loading: false });
    } catch (err) {
      set({ loading: false, error: toMessage(err, 'Đăng nhập thất bại') });
      throw err;
    }
  },

  register: async (email, password) => {
    set({ loading: true, error: null });
    try {
      const res = await authService.register(email, password);
      const user: AuthUser = { id: res.userId, email: res.email };
      persistSession(res.token, user);
      set({ token: res.token, user, isAuthenticated: true, loading: false });
    } catch (err) {
      set({ loading: false, error: toMessage(err, 'Đăng ký thất bại') });
      throw err;
    }
  },

  logout: () => {
    clearSession();
    set({ token: null, user: null, isAuthenticated: false, error: null });
  },

  loadFromStorage: () => {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    if (!token) {
      set({ token: null, user: null, isAuthenticated: false });
      return;
    }
    let user: AuthUser | null = null;
    const raw = localStorage.getItem(USER_STORAGE_KEY);
    if (raw) {
      try {
        const parsed = JSON.parse(raw) as Partial<AuthUser>;
        if (parsed && typeof parsed.id === 'string' && typeof parsed.email === 'string') {
          user = { id: parsed.id, email: parsed.email };
        }
      } catch {
        user = null;
      }
    }
    set({ token, user, isAuthenticated: true });
  },

  clearError: () => set({ error: null }),
}));
